/**
 * TemplateMarketplace - Buy, sell, and discover community templates
 */
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { 
  Store, Search, Star, Download, Plus, Filter,
  Loader2, DollarSign, TrendingUp, Sparkles, Tag
} from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function TemplateMarketplace() {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [templates, setTemplates] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [sortBy, setSortBy] = useState('popular');
  const [searchQuery, setSearchQuery] = useState('');
  const [freeOnly, setFreeOnly] = useState(false);

  useEffect(() => {
    fetchTemplates();
    fetchCategories();
  }, [selectedCategory, sortBy, freeOnly]);

  const fetchTemplates = async () => {
    setLoading(true);
    try {
      let url = `${API}/marketplace/templates?sort_by=${sortBy}&free_only=${freeOnly}`;
      if (selectedCategory) url += `&category=${selectedCategory}`;
      
      const response = await axios.get(url);
      setTemplates(response.data.templates);
    } catch (err) {
      console.error('Failed to fetch templates:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/marketplace/categories`);
      setCategories(response.data.categories);
    } catch (err) {
      console.error('Failed to fetch categories:', err);
    }
  };

  const filteredTemplates = templates.filter(t => 
    !searchQuery || 
    t.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    t.description.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-zinc-950 pt-24 pb-16">
      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <div className="w-16 h-16 bg-gradient-to-br from-teal-500/20 to-green-500/20 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <Store className="w-8 h-8 text-teal-400" />
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-white mb-2">
            Template Marketplace
          </h1>
          <p className="text-zinc-400 max-w-xl mx-auto mb-6">
            Discover community-created templates or sell your own. 
            Earn 80% of every sale.
          </p>

          <div className="flex flex-wrap justify-center gap-3">
            <Button
              onClick={() => navigate('/marketplace/create')}
              className="bg-gradient-to-r from-teal-500 to-green-500"
            >
              <Plus className="w-4 h-4 mr-2" />
              Create Template
            </Button>
            <Button
              onClick={() => navigate('/marketplace/my-templates')}
              variant="outline"
              className="border-zinc-700 text-zinc-300"
            >
              My Templates
            </Button>
            <Button
              onClick={() => navigate('/marketplace/earnings')}
              variant="outline"
              className="border-zinc-700 text-zinc-300"
            >
              <DollarSign className="w-4 h-4 mr-2" />
              Earnings
            </Button>
          </div>
        </motion.div>

        {/* Filters */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-zinc-900/50 border border-white/10 rounded-xl p-4 mb-8"
        >
          <div className="flex flex-wrap items-center gap-4">
            {/* Search */}
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                <Input
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search templates..."
                  className="pl-10 bg-zinc-800 border-zinc-700 text-white"
                />
              </div>
            </div>

            {/* Sort */}
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-2 text-white"
            >
              <option value="popular">Most Popular</option>
              <option value="newest">Newest</option>
              <option value="rating">Highest Rated</option>
              <option value="price_low">Price: Low to High</option>
              <option value="price_high">Price: High to Low</option>
            </select>

            {/* Free Only Toggle */}
            <button
              onClick={() => setFreeOnly(!freeOnly)}
              className={`px-4 py-2 rounded-lg border transition-colors ${
                freeOnly 
                  ? 'bg-teal-500/20 border-teal-500/50 text-teal-400' 
                  : 'bg-zinc-800 border-zinc-700 text-zinc-400'
              }`}
            >
              Free Only
            </button>
          </div>

          {/* Categories */}
          {categories.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-4 pt-4 border-t border-zinc-800">
              <button
                onClick={() => setSelectedCategory(null)}
                className={`px-3 py-1 rounded-full text-sm transition-colors ${
                  !selectedCategory 
                    ? 'bg-teal-500 text-white' 
                    : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700'
                }`}
              >
                All
              </button>
              {categories.map(cat => (
                <button
                  key={cat.id}
                  onClick={() => setSelectedCategory(cat.id)}
                  className={`px-3 py-1 rounded-full text-sm transition-colors ${
                    selectedCategory === cat.id 
                      ? 'bg-teal-500 text-white' 
                      : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700'
                  }`}
                >
                  {cat.name} ({cat.count})
                </button>
              ))}
            </div>
          )}
        </motion.div>

        {/* Templates Grid */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 text-teal-400 animate-spin" />
          </div>
        ) : filteredTemplates.length === 0 ? (
          <div className="text-center py-12">
            <Store className="w-12 h-12 text-zinc-600 mx-auto mb-4" />
            <p className="text-zinc-400 mb-4">No templates found</p>
            <Button onClick={() => navigate('/marketplace/create')} className="bg-teal-500">
              Create the First One
            </Button>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredTemplates.map((template, index) => (
              <motion.div
                key={template.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                className="bg-zinc-900/50 border border-white/10 rounded-xl p-6 hover:border-teal-500/30 transition-colors cursor-pointer group"
                onClick={() => navigate(`/marketplace/template/${template.id}`)}
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-teal-500/20 to-green-500/20 rounded-xl flex items-center justify-center">
                    <Sparkles className="w-6 h-6 text-teal-400" />
                  </div>
                  {template.price === 0 ? (
                    <span className="text-green-400 text-sm font-semibold bg-green-500/10 px-2 py-1 rounded">FREE</span>
                  ) : (
                    <span className="text-white font-semibold">${template.price.toFixed(2)}</span>
                  )}
                </div>

                <h3 className="text-lg font-semibold text-white mb-2 group-hover:text-teal-400 transition-colors">
                  {template.name}
                </h3>
                <p className="text-zinc-400 text-sm mb-4 line-clamp-2">
                  {template.description}
                </p>

                <div className="flex items-center justify-between text-sm">
                  <div className="flex items-center gap-4">
                    {template.avg_rating > 0 && (
                      <span className="flex items-center gap-1 text-yellow-400">
                        <Star className="w-4 h-4 fill-current" />
                        {template.avg_rating.toFixed(1)}
                      </span>
                    )}
                    <span className="flex items-center gap-1 text-zinc-500">
                      <Download className="w-4 h-4" />
                      {template.usage_count}
                    </span>
                  </div>
                  <span className="text-zinc-500">by {template.creator_name}</span>
                </div>

                {template.tags && template.tags.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-4">
                    {template.tags.slice(0, 3).map(tag => (
                      <span key={tag} className="text-xs bg-zinc-800 text-zinc-400 px-2 py-0.5 rounded">
                        {tag}
                      </span>
                    ))}
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        )}

        {/* Call to Action */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-gradient-to-r from-teal-500/10 to-green-500/10 border border-teal-500/20 rounded-xl p-8 mt-12 text-center"
        >
          <h2 className="text-2xl font-bold text-white mb-2">Create & Earn</h2>
          <p className="text-zinc-400 mb-6">
            Share your best prompts with the community and earn 80% of every sale.
          </p>
          <Button
            onClick={() => navigate('/marketplace/create')}
            className="bg-gradient-to-r from-teal-500 to-green-500"
          >
            <Plus className="w-4 h-4 mr-2" />
            Create Your First Template
          </Button>
        </motion.div>
      </div>
    </div>
  );
}
